import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, Search } from "lucide-react";
import { useEventBalances } from "@/hooks/events/useEventBalances";
import { StudentEventHistoryDialog } from "./StudentEventHistoryDialog";

export const StudentBalancesTable = () => {
  const { data = [], isLoading } = useEventBalances();
  const [search, setSearch] = useState("");
  const [historyFor, setHistoryFor] = useState<{ code: string; name: string } | null>(null);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data;
    return data.filter((r) =>
      [r.fishsix_student_code, r.first_name, r.last_name, r.nickname]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [data, search]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="ค้นหาด้วยรหัส / ชื่อ / ชื่อเล่น"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>
      <div className="border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>รหัส</TableHead>
              <TableHead>ชื่อ</TableHead>
              <TableHead className="text-right">คงเหลือ (ครั้ง)</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">กำลังโหลด...</TableCell></TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">ไม่มีนักเรียนที่มีคงเหลือ</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const fullName =
                `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() +
                (r.nickname ? ` (${r.nickname})` : "");
              return (
                <TableRow key={r.fishsix_student_code}>
                  <TableCell className="font-mono text-xs">{r.fishsix_student_code}</TableCell>
                  <TableCell>{fullName || "-"}</TableCell>
                  <TableCell className="text-right font-medium">{r.event_balance}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHistoryFor({ code: r.fishsix_student_code, name: fullName })}
                    >
                      <History className="h-4 w-4" /> ประวัติ
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <StudentEventHistoryDialog
        open={!!historyFor}
        onOpenChange={(v) => !v && setHistoryFor(null)}
        fishsixCode={historyFor?.code ?? null}
        studentName={historyFor?.name}
      />
    </div>
  );
};
